import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { TextField, SortMenu, Select, Button } from "@ann/web-components";
import {
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
  SearchContainer,
} from "./Filters.styled";
import {
  getAuditActionListFilter,
  getAuditSectionListFilter,
} from "../../../../../../../../../services/audit";

const Filters = ({
  onSortChange,
  onOrderChange,
  selectedSortOption = "timestamp",
  orderDirection = "desc",
  onSearchChange,
  onRoleChange,
  onActionChange,
  onSectionChange,
  loading = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [filtersKey, setFiltersKey] = useState(0);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [actionOptions, setActionOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [actionsResponse, sectionsResponse] = await Promise.all([
          getAuditActionListFilter({ pageSize: 50 }),
          getAuditSectionListFilter({ pageSize: 50 }),
        ]);

        if (actionsResponse?.body) {
          const actions = (actionsResponse.body || []).map((item) => ({
            value: item.action || "",
            label: item.action || "",
          }));
          setActionOptions(actions);
        }

        if (sectionsResponse?.body) {
          const sections = (sectionsResponse.body || []).map((item) => ({
            value: item.businessEntityName || item.name || "",
            label: item.businessEntityName || item.name || "",
          }));
          setSectionOptions(sections);
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };

    fetchFilters();
  }, []);

  const handleSearchChange = useCallback(
    (value) => {
      setSearchValue(value);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        onSearchChange?.(value);
      }, 500);
    },
    [onSearchChange]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleActionChange = (value, option) => {
    const actionValue = value === null ? null : value;
    setSelectedAction(
      actionValue === null ? { label: "All Actions", value: null } : option
    );
    onActionChange?.(actionValue);
  };

  const handleResourceChange = (value, option) => {
    const resourceValue = value === null ? null : value;
    setSelectedResource(
      resourceValue === null ? { label: "All Resources", value: null } : option
    );
    onSectionChange?.(resourceValue);
  };

  const handleClearFilters = useCallback(() => {
    setSelectedAction(null);
    setSelectedResource(null);
    setSearchValue("");
    setFiltersKey((prev) => prev + 1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    onActionChange?.(null);
    onSectionChange?.(null);
    onSearchChange?.("");
  }, [onActionChange, onSectionChange, onSearchChange]);

  const hasActiveFilters = useMemo(() => {
    const hasSearch = searchValue && searchValue.trim() !== "";
    const hasAction = selectedAction && selectedAction.value !== null;
    const hasResource = selectedResource && selectedResource.value !== null;
    return !!(hasSearch || hasAction || hasResource);
  }, [searchValue, selectedAction, selectedResource]);

  const allActionOptions = useMemo(() => {
    return [{ label: "All Actions", value: null }, ...actionOptions];
  }, [actionOptions]);

  const allResourceOptions = useMemo(() => {
    return [{ label: "All Resources", value: null }, ...sectionOptions];
  }, [sectionOptions]);

  return (
    <FiltersContainer>
      <FiltersLeftContent>
        <SearchContainer>
          <TextField
            iconLeft="search"
            onChange={handleSearchChange}
            size="small"
            placeholder="Search"
            value={searchValue}
            disabled={loading}
          />
        </SearchContainer>

        <Select
          key={`action-filter-${filtersKey}`}
          label="Action"
          placeholder={selectedAction?.label || "All Actions"}
          onChange={handleActionChange}
          size="small"
          width="auto"
          minWidth="120px"
          disabled={loading}
        >
          <Select.Menu>
            {allActionOptions.map((action) => (
              <Select.Item
                key={action.value || "all"}
                value={action.value}
                label={action.label}
              >
                {action.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>

        <Select
          key={`resource-filter-${filtersKey}`}
          label="Resource"
          placeholder={selectedResource?.label || "All Resources"}
          onChange={handleResourceChange}
          size="small"
          width="auto"
          minWidth="140px"
          disabled={loading}
        >
          <Select.Menu>
            {allResourceOptions.map((resource) => (
              <Select.Item
                key={resource.value || "all"}
                value={resource.value}
                label={resource.label}
              >
                {resource.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>

        {hasActiveFilters && (
          <Button
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearFilters}
            disabled={loading}
          />
        )}
      </FiltersLeftContent>

      <FiltersRightContent>
        <SortMenu
          minWidth="200px"
          onChange={(id) => {
            if (onSortChange) {
              onSortChange(id);
            }
          }}
          order={orderDirection ? orderDirection.toLowerCase() : "desc"}
          onOrderChange={(newOrder) => {
            if (onOrderChange) {
              onOrderChange(newOrder);
            }
          }}
          options={[
            {
              icon: "clock",
              id: "timestamp",
              label: "Most Recent",
            },
            {
              icon: "sortByAlpha",
              id: "userName",
              label: "User",
            },
            {
              icon: "label",
              id: "action",
              label: "Action",
            },
            {
              icon: "category",
              id: "resource",
              label: "Resource",
            },
          ]}
          selected={selectedSortOption || "timestamp"}
          size="medium"
          title="SORT BY"
          width="240px"
          disabled={loading}
        />
      </FiltersRightContent>
    </FiltersContainer>
  );
};

export default Filters;
